import { useState, Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import { Boxes, Gauge, Layers, Ruler, Info, ChevronRight } from "lucide-react";
import Layout from "@/components/Layout";
import { Panel, StatCard, LoadingCards, LoadingPanel, EmptyState, SortTh, useTableSort } from "@/components/widgets";
import { useFilter } from "@/context/FilterContext";
import client from "@/lib/api";
import { fmtKg, fmtInt, fmtPct, fmtTonnes } from "@/lib/format";

export default function Inventory() {
  const { query, reset } = useFilter();
  const [openGrades, setOpenGrades] = useState({});

  const { data, isLoading } = useQuery({
    queryKey: ["inv", query],
    queryFn: () => client.get(`/analytics/inventory?${query}`).then((r) => r.data.data),
  });

  const c = data?.consumption;
  const { sorted, sort, dir, onSort } = useTableSort(data?.byGradeThickness, "weightKg", "desc", {
    gradeLabel: (r) => r.gradeLabel, weightKg: (r) => r.weightKg, recordCount: (r) => r.recordCount,
    thicknessCount: (r) => r.thicknesses.length,
  });

  const toggle = (id) => setOpenGrades((o) => ({ ...o, [id]: !o[id] }));
  const empty = data && (data.byGradeThickness || []).length === 0;

  return (
    <Layout title="Inventory — Material Consumption" subtitle="Usage by material grade and thickness for the selected period & filters">
      {isLoading ? (
        <div className="space-y-4"><LoadingCards /><LoadingPanel /></div>
      ) : !data ? (
        <Panel><EmptyState onReset={reset} /></Panel>
      ) : (
        <div className="space-y-5 animate-fade-up">
          <div className="flex items-start gap-2 text-xs text-slate-600 bg-violet-50 border rounded-lg px-4 py-2.5" style={{ borderColor: "#C4B5FD" }} data-testid="inventory-disclaimer">
            <Info size={15} className="text-violet-700 shrink-0 mt-0.5" />
            <span>Consumption is drawn from confirmed nesting history for the active filters. Use the <b>Filters</b> button to narrow by period, machine, customer, ownership, thickness or programmer — the breakdown below updates to match.</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Material Consumed" value={fmtTonnes(c.consumedWeightKg)} unit="t" accent="#7C3AED"
              icon={Boxes} sub={`${fmtKg(c.consumedWeightKg)} kg total`} testid="kpi-consumed" />
            <StatCard label="Avg Daily Use" value={fmtKg(c.avgDailyUseKg)} unit="kg" accent="#0D9488"
              icon={Gauge} sub={`over ${fmtInt(c.spanDays)} days`} testid="kpi-daily-use" />
            <StatCard label="Active Grades" value={fmtInt(c.activeGrades)} accent="#D97706"
              icon={Layers} sub={`${fmtInt(c.recordCount)} nesting records`} testid="kpi-active-grades" />
            <StatCard label="Thicknesses Used" value={fmtInt(c.thicknessCount)} accent="#2563EB"
              icon={Ruler} sub="distinct thickness values" testid="kpi-thickness-count" />
          </div>

          <Panel className="!p-0 overflow-hidden" testid="consumption-panel">
            <div className="px-4 sm:px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "#E2E8F0" }}>
              <h3 className="font-head text-base font-semibold">Consumption by Material & Thickness</h3>
              <span className="text-xs text-slate-500">{fmtInt((sorted || []).length)} grades · click a row to expand thicknesses</span>
            </div>
            {empty ? (
              <EmptyState onReset={reset} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-xs text-slate-500 border-b" style={{ borderColor: "#E2E8F0" }}>
                      <th className="py-2.5 px-3 font-medium w-8"></th>
                      <SortTh label="Material Grade" k="gradeLabel" sort={sort} dir={dir} onSort={onSort} />
                      <SortTh label="Thicknesses" k="thicknessCount" sort={sort} dir={dir} onSort={onSort} right />
                      <SortTh label="Records" k="recordCount" sort={sort} dir={dir} onSort={onSort} right />
                      <SortTh label="Consumed (kg)" k="weightKg" sort={sort} dir={dir} onSort={onSort} right />
                      <th className="py-2.5 px-3 font-medium text-right">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(sorted || []).map((g) => {
                      const isOpen = !!openGrades[g.gradeId];
                      return (
                        <Fragment key={g.gradeId}>
                          <tr onClick={() => toggle(g.gradeId)} data-testid={`grade-row-${g.gradeId}`}
                            className="border-b hover:bg-violet-50/40 cursor-pointer" style={{ borderColor: "#F1F5F9" }}>
                            <td className="py-2.5 px-3">
                              <ChevronRight size={15} className="text-slate-400 transition-transform" style={{ transform: isOpen ? "rotate(90deg)" : "none" }} />
                            </td>
                            <td className="py-2.5 px-3 font-medium text-slate-800">{g.gradeLabel}</td>
                            <td className="py-2.5 px-3 text-right font-mono text-slate-500">{g.thicknesses.length}</td>
                            <td className="py-2.5 px-3 text-right font-mono text-slate-500">{fmtInt(g.recordCount)}</td>
                            <td className="py-2.5 px-3 text-right font-mono font-semibold text-slate-800">{fmtKg(g.weightKg)}</td>
                            <td className="py-2.5 px-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="h-1.5 w-16 rounded-full bg-slate-100 overflow-hidden hidden sm:block">
                                  <div className="h-full rounded-full" style={{ width: `${g.sharePercent}%`, background: "#7C3AED" }} />
                                </div>
                                <span className="font-mono text-xs text-slate-500 w-12">{fmtPct(g.sharePercent)}</span>
                              </div>
                            </td>
                          </tr>
                          {isOpen && g.thicknesses.map((t) => (
                            <tr key={g.gradeId + t.thicknessId} className="border-b bg-slate-50/60" style={{ borderColor: "#F1F5F9" }} data-testid={`thk-row-${t.thicknessId}`}>
                              <td></td>
                              <td className="py-2 px-3 pl-8 text-slate-500">
                                <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-white border" style={{ borderColor: "#E2E8F0" }}>{t.label}</span>
                              </td>
                              <td></td>
                              <td className="py-2 px-3 text-right font-mono text-xs text-slate-500">{fmtInt(t.recordCount)}</td>
                              <td className="py-2 px-3 text-right font-mono text-slate-700">{fmtKg(t.weightKg)}</td>
                              <td className="py-2 px-3 text-right font-mono text-xs text-slate-400">{fmtPct(t.sharePercent)}</td>
                            </tr>
                          ))}
                        </Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 bg-slate-50 font-semibold" style={{ borderColor: "#CBD5E1" }} data-testid="consumption-totals">
                      <td></td>
                      <td className="py-2.5 px-3 text-slate-800">Total ({fmtInt((sorted || []).length)} grades)</td>
                      <td className="py-2.5 px-3 text-right font-mono text-slate-600">{fmtInt(c.thicknessCount)}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-slate-600">{fmtInt(c.recordCount)}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-slate-900">{fmtKg(c.consumedWeightKg)}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-xs text-slate-500">100.0%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Panel>
        </div>
      )}
    </Layout>
  );
}
